import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { IoChevronDown } from "react-icons/io5";
import { buildAutomationChildTestId, buildAutomationOptionTestId, resolveAutomationTestId } from "../../InputField/testId";
import useFloatingPanel from "./useFloatingPanel";

const MultiSelectDropdown = ({
  label,
  options = [],
  value = [],
  onChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  className = "",
  disabled = false,
  loading = false,
  testId,
  id,
  "data-testid": dataTestId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef(null);
  const controlRef = useRef(null);
  const searchRef = useRef(null);
  const optionRefs = useRef([]);
  const listId = useId();
  const fieldTestId = resolveAutomationTestId(dataTestId, testId, id);
  const selectedValues = Array.isArray(value) ? value : [];
  const panelStyle = useFloatingPanel(isOpen, controlRef);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      return undefined;
    }
    const frame = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((option) =>
      `${option.label ?? option.value ?? ""}`.toLowerCase().includes(needle)
    );
  }, [options, query]);

  useEffect(() => {
    optionRefs.current = [];
  }, [filteredOptions]);

  useEffect(() => {
    if (!isOpen) return;
    if (!filteredOptions.length) {
      setHighlightIndex(-1);
      return;
    }
    setHighlightIndex(0);
  }, [filteredOptions.length, isOpen, query]);

  useEffect(() => {
    if (!isOpen) return;
    const node = optionRefs.current[highlightIndex];
    node?.scrollIntoView?.({ block: "nearest" });
  }, [highlightIndex, isOpen]);

  const selectedLabels = useMemo(() => (
    options
      .filter((option) => selectedValues.includes(option.value))
      .map((option) => option.label ?? option.value ?? "")
  ), [options, selectedValues]);

  const displayValue = useMemo(() => {
    if (!selectedLabels.length) return placeholder;
    if (selectedLabels.length <= 2) return selectedLabels.join(", ");
    return `${selectedLabels.length} selected`;
  }, [placeholder, selectedLabels]);

  const toggleOption = (optionValue) => {
    if (!onChange || disabled) return;
    if (selectedValues.includes(optionValue)) {
      onChange(selectedValues.filter((item) => item !== optionValue));
    } else {
      onChange([...selectedValues, optionValue]);
    }
  };

  const handleSelectAll = () => {
    if (!onChange || disabled) return;
    onChange(options.map((option) => option.value));
  };

  const handleClearAll = () => {
    if (!onChange || disabled) return;
    onChange([]);
  };

  const moveHighlight = (delta) => {
    if (!filteredOptions.length) return;
    setHighlightIndex((prev) => {
      const base = prev < 0 ? 0 : prev;
      return (base + delta + filteredOptions.length) % filteredOptions.length;
    });
  };

  const selectHighlighted = () => {
    const option = filteredOptions[highlightIndex];
    if (option) toggleOption(option.value);
  };

  const handleControlKeyDown = (event) => {
    if (disabled) return;
    if (!isOpen && (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      setIsOpen(true);
      return;
    }
    if (!isOpen) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveHighlight(1);
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveHighlight(-1);
    }
    if (event.key === "Enter") {
      event.preventDefault();
      selectHighlighted();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
      controlRef.current?.focus();
    }
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveHighlight(1);
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveHighlight(-1);
    }
    if (event.key === "Enter") {
      event.preventDefault();
      selectHighlighted();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
      controlRef.current?.focus();
    }
  };

  return (
    <div className={`account-search-dropdown ${className}`.trim()} ref={containerRef} data-testid={fieldTestId}>
      {label ? <div className="account-search-dropdown__label">{label}</div> : null}
      <button
        type="button"
        ref={controlRef}
        className={`account-search-dropdown__control ${isOpen ? "is-open" : ""} ${disabled ? "is-disabled" : ""}`.trim()}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        onKeyDown={handleControlKeyDown}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listId}
        disabled={disabled}
        data-testid={buildAutomationChildTestId(fieldTestId, "toggle")}
      >
        <span className={`account-search-dropdown__value ${selectedLabels.length ? "" : "is-placeholder"}`.trim()}>
          {loading && !selectedLabels.length ? "Loading…" : displayValue}
        </span>
        <span className="account-search-dropdown__icon" aria-hidden="true">
          <IoChevronDown size={16} />
        </span>
      </button>
      {isOpen ? (
        <div
          className="account-search-dropdown__panel account-search-dropdown__panel--floating"
          style={panelStyle || undefined}
          data-testid={buildAutomationChildTestId(fieldTestId, "menu")}
        >
          <div className="account-search-dropdown__panel-header">
            <span className="account-search-dropdown__count">
              {selectedLabels.length ? `${selectedLabels.length} selected` : "No selection"}
            </span>
            <div className="account-search-dropdown__actions">
              <button type="button" onClick={handleSelectAll} data-testid={buildAutomationChildTestId(fieldTestId, "select-all")}>
                Select all
              </button>
              <button type="button" onClick={handleClearAll} data-testid={buildAutomationChildTestId(fieldTestId, "clear")}>
                Clear
              </button>
            </div>
          </div>
          <input
            ref={searchRef}
            className="account-search-dropdown__search"
            type="text"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            data-testid={buildAutomationChildTestId(fieldTestId, "input")}
          />
          <div
            className="account-search-dropdown__options"
            role="listbox"
            aria-multiselectable="true"
            id={listId}
          >
            {loading && !options.length ? (
              <div className="account-search-dropdown__empty account-search-dropdown__loading" data-testid={buildAutomationChildTestId(fieldTestId, "loading")}>
                Loading…
              </div>
            ) : filteredOptions.length ? (
              filteredOptions.map((option, index) => {
                const checked = selectedValues.includes(option.value);
                const isHighlighted = highlightIndex === index;
                const optionTestId = buildAutomationOptionTestId(fieldTestId, option.value ?? option.label);
                return (
                  <button
                    key={option.value}
                    type="button"
                    ref={(node) => {
                      optionRefs.current[index] = node;
                    }}
                    className={`account-search-dropdown__option ${checked ? "is-selected" : ""} ${isHighlighted ? "is-highlighted" : ""}`.trim()}
                    onClick={() => toggleOption(option.value)}
                    role="option"
                    aria-selected={checked}
                    data-testid={optionTestId}
                  >
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={checked}
                      readOnly
                      data-testid={buildAutomationChildTestId(optionTestId, "input")}
                    />
                    <span>{option.label ?? option.value}</span>
                  </button>
                );
              })
            ) : (
              <div className="account-search-dropdown__empty" data-testid={buildAutomationChildTestId(fieldTestId, "empty")}>No matches</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default MultiSelectDropdown;
